import axios, { CancelTokenSource } from "axios";
import { extend } from "quasar";
import { ActionTree } from "vuex";
import { StateInterface } from "src/store";
import { DatasetStateInterface } from "./state";

import API from "src/services/api/api";
import {
  ObjectItem,
  ObjectType,
  Tag,
  ClassItem,
  ClassGroupItem,
} from "../../models";
import { ObjectsGetListRequest } from "src/services/api/sqlr-dataset/dto";

let getObjectItemsCancelToken: CancelTokenSource;

const actions: ActionTree<DatasetStateInterface, StateInterface> = {
  async init(context) {
    if (!context.state.inited) {
      await context.dispatch("initObjectTypes");
      await context.dispatch("initStorages");
      await context.dispatch("initSources");
      await context.dispatch("initTags");
      await context.dispatch("initClasses");
      await context.dispatch("initClassGroups");
      await context.dispatch("initMarkupTypes");
      context.commit("init", true);
    }
  },

  //#region objects
  async getObjectName(context, obj_guid: string) {
    const index = context.state.cacheObjectItems.findIndex(
      (x) => x.obj_guid == obj_guid,
    );
    if (index != -1) {
      // console.log("getObjectName", context.state.cacheObjectItems[index].uri);
      return context.state.cacheObjectItems[index].uri;
    } else {
      const objectItem = await context.dispatch("getObjectItem", { obj_guid });
      // console.log("getObjectName", objectItem.uri);
      return objectItem.uri;
    }
  },

  async getObjectItems(context, params: ObjectsGetListRequest) {
    if (getObjectItemsCancelToken) {
      getObjectItemsCancelToken.cancel("Произошел повторный вызов");
    }
    getObjectItemsCancelToken = axios.CancelToken.source();
    const objectItems = await API.sqlrDataset.data_objects_get_list(
      params,
      getObjectItemsCancelToken,
    );
    context.commit("setObjectItems", objectItems);
    for (const objectItem of objectItems) {
      context.commit("cacheObjectItem", objectItem);
    }
    return objectItems;
  },

  deleteObjectItems(context, obj_guid: string[]) {
    if (getObjectItemsCancelToken) {
      getObjectItemsCancelToken.cancel("Произошел повторный вызов");
    }
    getObjectItemsCancelToken = axios.CancelToken.source();
    API.sqlrDataset
      .data_objects_delete(obj_guid, getObjectItemsCancelToken)
      .then(() => {
        context.commit("deleteObjectItems", obj_guid);
      });
  },

  async getObjectItemsCount(context, params: ObjectsGetListRequest) {
    const count = await API.sqlrDataset.data_objects_get_count(params);
    return count;
  },
  async getObjectItem(context, params) {
    const data = await API.sqlrDataset.data_object_get(params.obj_guid);
    if (data) {
      context.commit("setObjectItem", data);
      context.commit("cacheObjectItem", data);
    }
    return extend(true, {}, data);
  },
  async saveObjectItem(context, objectItem: ObjectItem) {
    const obj_guid = await API.sqlrDataset.data_object_save(objectItem);
    if (obj_guid) {
      const objectItemData = { ...objectItem, obj_guid: obj_guid };
      context.commit("setObjectItem", objectItemData);
      context.commit("cacheObjectItem", objectItemData);
    }
    return obj_guid;
  },
  async setObjectsClasses(context, payload) {
    await API.sqlrDataset.data_objects_set_classes(payload);
    context.commit("setObjectsClasses", payload);
  },
  async removeObjectsClasses(context, payload) {
    await API.sqlrDataset.data_objects_remove_classes(payload);
    context.commit("removeObjectsClasses", payload);
  },
  //#endregion

  //#region object_types
  async initObjectTypes(context) {
    const objectTypes = await API.sqlrDataset.data_object_types_get_list();
    context.commit("setObjectTypes", objectTypes);
  },
  async saveObjectType(context, objectType: ObjectType) {
    await API.sqlrDataset.data_object_type_save(objectType);
    await context.dispatch("initObjectTypes");
  },
  //#endregion

  async initStorages(context) {
    const storages = await API.sqlrDataset.data_storages_get_list();
    context.commit("setStorages", storages);
  },

  async initSources(context) {
    const sources = await API.sqlrDataset.data_sources_get_list();
    context.commit("setSources", sources);
  },

  async initMarkupTypes(context) {
    const markupTypes = await API.sqlrDataset.data_markup_types_get_list();
    context.commit("setMarkupTypes", markupTypes);
  },

  //#region tags
  async initTags(context) {
    const tags = await API.sqlrDataset.data_tags_get_list();
    context.commit("setTags", tags);
  },
  async saveTag(context, item: Tag) {
    item.tag_guid = await API.sqlrDataset.data_tag_save(item);
    context.commit("setTag", item);
  },
  async deleteTag(context, tag_guid: string) {
    await API.sqlrDataset.data_tag_delete(tag_guid);
    context.commit("deleteTag", tag_guid);
  },
  async deleteObjectsByTag(context, tag_guid: string) {
    await API.sqlrDataset.data_tag_delete_with_objects(tag_guid);
    context.commit("deleteTag", tag_guid);
  },
  setTagParent(context, payload: { child_guid: string; parent_guid: string }) {
    const getChildrenRecursively = (tag_guid: string): Tag[] => {
      const result = context.state.tags.filter(
        (x) => x.parent_guid == tag_guid,
      );
      const children = [];
      for (const child of result) {
        children.push(...getChildrenRecursively(child.tag_guid));
      }
      return [...result, ...children];
    };
    const children = getChildrenRecursively(payload.child_guid);
    // запрещаем перенос в себя и в дочерние папки
    if (
      payload.child_guid != payload.parent_guid &&
      !children.some((x) => x.tag_guid == payload.parent_guid)
    ) {
      context.commit("setTagParent", payload);
      const index = context.state.tags.findIndex(
        (x) => x.tag_guid == payload.child_guid,
      );
      if (index != -1) {
        context.dispatch("saveTag", context.state.tags[index]);
      }
    }
  },
  //#endregion

  //#region classes
  async initClasses(context) {
    const classes = await API.sqlrDataset.data_classes_get_list();
    context.commit("setClasses", classes);
  },
  async saveClass(context, item: ClassItem) {
    item.class_guid = await API.sqlrDataset.data_class_save(item);
    context.commit("setClass", item);
  },
  async deleteClass(context, class_guid: string) {
    await API.sqlrDataset.data_class_delete(class_guid);
    context.commit("deleteClass", class_guid);
  },
  //#endregion

  //#region class_groups
  async initClassGroups(context) {
    const class_groups = await API.sqlrDataset.data_class_groups_get_list();
    context.commit("setClassGroups", class_groups);
  },
  async saveClassGroup(context, item: ClassGroupItem) {
    item.class_group_guid = await API.sqlrDataset.data_class_group_save({
      ...item,
      classes: item.classes,
    });
    context.commit("setClassGroup", item);
  },
  async deleteClassGroup(context, class_group_guid: string) {
    await API.sqlrDataset.data_class_group_delete(class_group_guid);
    context.commit("deleteClassGroup", class_group_guid);
  },
  //#endregion
};

export default actions;
